import { registerDecorator, ValidateIf, ValidationOptions, ValidationArguments } from '@lgce/class-validator';
import { OfferType } from './types/offer-type';
import { triggerSequences } from './types/trigger-sequences';

// tslint:disable:only-arrow-functions

/**
 * Mark the property as optional if the function returns truthy
 */
export function IsOptionalIf(allowOptional: (obj: any, value: any) => boolean, options?: ValidationOptions): PropertyDecorator {
  // If required, do validate. Otherwise if null|undefined, don't validate
  return ValidateIf((obj, value: any) => !allowOptional(obj, value) || value != null, options);
}

function getTriggerSequenceByType(type: string): { id: string; type: string; hasConfig?: boolean } | undefined {
  return triggerSequences.find((ts) => ts.id === type);
}

export function IsValidForSAPOfferType(offerTypeKey = 'offerType', validationOptions?: ValidationOptions): any {
  return function(object: Record<string, unknown>, propertyName: string): void {
    registerDecorator({
      name: 'isValidForSAPOfferType',
      target: object.constructor,
      propertyName,
      constraints: [offerTypeKey],
      options: validationOptions,
      validator: {
        validate(value: {
          alarmCycles: {
            triggerSequence: {
              id: string;
              type: string;
              hasConfig?: boolean;
            };
          }[];
        },       args: ValidationArguments): boolean {
          const [offerTypeK]: [string] = args.constraints as [string];
          const model = args.object as Record<string, unknown>;

          if (model[offerTypeK] === OfferType.SAP) {
            const automaticCycles = value.alarmCycles.filter((alarmCycle) => {
              const triggerSequence = alarmCycle.triggerSequence;

              const ts = getTriggerSequenceByType(triggerSequence.type);

              if (!ts) {
                return true;
              }

              return ts.type === 'auto';

            }) ;

            return automaticCycles.length === 0;
          } else {
            return true;
          }
        },
      },
    });
  };
}

export function BusinessPasswordCheck(validationOptions?: ValidationOptions): any {
  return function(object: Record<string, unknown>, propertyName: string): void {
    registerDecorator({
      name: 'businessPasswordCheck',
      target: object.constructor,
      propertyName,
      constraints: [],
      options: validationOptions,
      validator: {
        validate(value: string): boolean {
          if (!value) {
            return false;
          }

          const rules = [
            atLeastOneUpperCaseLetter,
            atLeastOneLowerCaseLetter,
            atLeastOneNumber,
            atLeastOneSpecialChar,
          ];
          const numberOfRulesToMatch = 3;

          return matchAtLeastXOfRules(value, rules, numberOfRulesToMatch);
        },
      },
    });
  };
}

function matchAtLeastXOfRules(value: string, rules: ((v: string) => boolean)[], atLeast = 1): boolean {
  let matchedRules = 0;

  if (rules.length === 0) {
    return true;
  }

  for (const rule of rules) {
    matchedRules += rule(value) ? 1 : 0;

    if (matchedRules === atLeast) {
      return true;
    }
  }

  return false;
}

function atLeastOneUpperCaseLetter(value: string): boolean {
  return !!/[A-Z]/.exec(value);
}

function atLeastOneLowerCaseLetter(value: string): boolean {
  return !!/[a-z]/.exec(value);
}

function atLeastOneNumber(value: string): boolean {
  return !!/[0-9]/.exec(value);
}

function atLeastOneSpecialChar(value: string): boolean {
  return !!/[!@#$%^&*]/.exec(value);
}

export function ConfirmPassword(field: string, validationOptions?: ValidationOptions): any {
  return function(object: Record<string, unknown>, propertyName: string): void {
    registerDecorator({
      name: 'confirmPassword',
      target: object.constructor,
      propertyName,
      constraints: [field],
      options: validationOptions,
      validator: {
        validate(value: string, args: ValidationArguments): boolean {
          const model = args.object as Record<string, unknown>;
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          const valueToMatch = model[args.constraints[0]];

          return value === valueToMatch;
        }
      }
    });
  };
}
